import { Shield, Info, AlertTriangle } from "lucide-react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "../../../components/ui/alert";
import { cn } from "@/lib/utils";

/**
 * SignaturePreview Component Props
 */
export interface SignaturePreviewProps {
  /**
   * The challenge message to be signed
   */
  message: string;
  /**
   * Wallet address that will sign the message
   */
  walletAddress: string;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Whether to show security information
   * @default true
   */
  showSecurityInfo?: boolean;
}

/**
 * SignaturePreview Component
 *
 * Displays a preview of the message that will be signed by the wallet.
 * Provides security assurance messaging and clear explanation of what signing means.
 *
 * Features:
 * - Clear display of the challenge message
 * - Security information about signing
 * - Wallet address confirmation
 * - Professional, trustworthy design
 *
 * @example
 * ```tsx
 * <SignaturePreview
 *   message="Sign this message to authenticate with EverReal..."
 *   walletAddress="7XwM...9kLp"
 * />
 * ```
 */
export function SignaturePreview({
  message,
  walletAddress,
  className,
  showSecurityInfo = true,
}: SignaturePreviewProps) {
  return (
    <div className={cn("space-y-4", className)}>
      {/* Message Preview Card */}
      <div className="rounded-lg border border-border bg-surface-container-low p-6 space-y-4">
        <div className="flex items-start gap-3">
          <Shield
            className="w-5 h-5 text-primary mt-0.5 flex-shrink-0"
            aria-hidden="true"
          />
          <div className="flex-1 space-y-2">
            <h3 className="text-base font-semibold text-primary">
              Message to Sign
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Please review this message carefully before signing.
            </p>
          </div>
        </div>

        {/* Message Content */}
        <div className="mt-4 p-4 rounded-md bg-surface-container border border-border">
          <pre className="text-sm text-foreground font-mono whitespace-pre-wrap break-words">
            {message}
          </pre>
        </div>

        {/* Wallet Address Confirmation */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>Signing with:</span>
          <code className="px-2 py-1 rounded bg-surface-container text-primary font-mono">
            {walletAddress}
          </code>
        </div>
      </div>

      {/* Security Information */}
      {showSecurityInfo && (
        <div className="space-y-3">
          {/* What Signing Means */}
          <Alert className="border-sapphire-base/30 bg-sapphire-dark/20">
            <Info className="h-4 w-4 text-sapphire-light" />
            <AlertTitle className="text-sapphire-light text-sm">
              What does signing mean?
            </AlertTitle>
            <AlertDescription className="text-muted-foreground text-xs space-y-1">
              <p>
                Signing this message proves you own this wallet without
                revealing your private key. This is a secure, free operation
                that does not involve any blockchain transaction.
              </p>
            </AlertDescription>
          </Alert>

          {/* Security Assurance */}
          <Alert className="border-border bg-primary/10">
            <Shield className="h-4 w-4 text-primary" />
            <AlertTitle className="text-primary text-sm">
              Security Assurance
            </AlertTitle>
            <AlertDescription className="text-muted-foreground text-xs space-y-1">
              <ul className="space-y-1 list-disc list-inside">
                <li>This signature cannot move funds from your wallet</li>
                <li>Your private key remains secure in your wallet</li>
                <li>This message is unique and cannot be reused</li>
              </ul>
            </AlertDescription>
          </Alert>

          {/* Warning */}
          <Alert
            variant="destructive"
            className="border-warning/50 bg-warning-dark/20"
          >
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle className="text-sm">
              Never share this signature
            </AlertTitle>
            <AlertDescription className="text-xs">
              Only sign messages from trusted applications. Never share your
              signature with third parties.
            </AlertDescription>
          </Alert>
        </div>
      )}
    </div>
  );
}

/**
 * Compact variant without detailed security information
 */
export function SignaturePreviewCompact(
  props: Omit<SignaturePreviewProps, "showSecurityInfo">,
) {
  return <SignaturePreview {...props} showSecurityInfo={false} />;
}
